import { nextTick } from 'vue';
import { createStore } from 'vuex';

export default createStore({
    state: {
        retractedSidebar: false,
        lastSavedStaffList: null,
        staffList: {},
        selectedEpisode: null,
        editMode: false,
        settingsMode: false,
        statsMode: false,
        summaryMode: false,

        prompts: [],
        alerts: [],
        confirms: [],
        conflicts: [],
        dualLanguageInputs: [],
        potentialMistakes: [],
        linkToPageModals: [],
        notificationZIndexCounter: 50,
        potentialMistakeSkips: new Set(),
        
        translationQueue: [],
        translationTotal: 0,

        // Popup state
        popupShown: false,
        popupMessage: "",
        popupType: "success",

        mode: null,
        myId: false,
        loggedIn: false,
        isConfirmed: false,
        isContributor: false,
        avatar: null,
        name: null,
        screenName: null,
        bio: null,
        discordId: null,
        twitterId: null,
        blueskyId: null,
        discordVisibility: 0,
        twitterVisibility: 0,
        blueskyVisibility: 0,
        
        // ID Blacklist for privacy
        idBlacklist: new Set(),
        
        // Translation cache for translate.php requests
        keyFrameTranslationCache: {},
        // Translation cache for AniList requests
        // aniListTranslationCache: {},

        // Interface
        nativeSize: localStorage.getItem('nativeSize') ? parseFloat(localStorage.getItem('nativeSize')) : 14 / 16,
        nativeFont: localStorage.getItem('nativeFont') || 'inter',
        englishSize: localStorage.getItem('englishSize') ? parseFloat(localStorage.getItem('englishSize')) : 14 / 16,
        englishFont: localStorage.getItem('englishFont') || 'inter',

        searchSystem: localStorage.getItem('searchSystem') || 'new',
        staffPageProvider: localStorage.getItem('staffPageProvider') || 'kfsl',
        defaultEpisode: localStorage.getItem('defaultEpisode') || 'overview',
        autoUnderline: localStorage.getItem('autoUnderline') || '',
        showTooltip: localStorage.getItem('showTooltip') || 'view',
        showTotalEpisodeCount: localStorage.getItem('showTotalEpisodeCount') ?? '1',
        airedDateFormat: localStorage.getItem('airedDateFormat') || 'full_en-GB',
        showNativeTitle: localStorage.getItem('showNativeTitle') ?? '1',

        themeMode: 'system',
        resolvedTheme: 'light',
    },
    mutations: {
        setMode(state, payload) {
            state.mode = payload;
        },
        selectEpisode(state, payload) {
            const { episode, scroll, scrollToInQueue } = payload;

            state.selectedEpisode = episode;
            state.settingsMode = false;
            state.statsMode = false;
            state.summaryMode = false;

            if (!episode) {
                return;
            }
            
            if (scroll) {
                nextTick(() => {
                    if (!document.querySelector('.menu-container .episode.active')) {
                        return;
                    }

                    // Scroll menu into view
                    const containerWidth = document.querySelector('.menu-container').offsetWidth;
                    const activeWidth = document.querySelector('.menu-container .episode.active').offsetWidth;
                    const activeX = document.querySelector('.menu-container .episode.active').offsetLeft;

                    document.querySelector('.menu-container').scrollTo({
                        left: activeX - containerWidth / 2 + activeWidth / 2,
                        behavior: 'smooth',
                    });

                    if (!scrollToInQueue) {
                        return;
                    }

                    // Scroll so the first staff member in queue is at the middle of the screen
                    const firstTranslatedStaff = document.querySelector('.in-queue');
                    if (firstTranslatedStaff) {
                        const pos = document.querySelector('.in-queue').getBoundingClientRect();
                        scrollTo({
                            top: pos.top + window.scrollY - window.innerHeight / 2 + pos.height / 2,
                            behavior: 'smooth'
                        });
                    }
                });
            }

            const selectedEpisodeIsTheFirst = episode === state.staffList.menus[0];

            // Update URL hash
            if (!location.hash && selectedEpisodeIsTheFirst) {
                return;
            }

            setTimeout(() => {
                const fixedName = (episode.name || "").replace(/^#/, "");

                window.history.pushState(null, null, `#${fixedName}`);
            });
        },
        setEditMode(state, payload) {
            state.editMode = !!payload;
        },
        toggleEditMode(state) {
            state.editMode = !state.editMode;
            
            // Rescroll into active episode if needed
            setTimeout(() => {
                if (document.querySelector(".episode.active")) {
                    if (document.querySelector(".episode.active").scrollIntoViewIfNeeded) {
                        document.querySelector(".episode.active").scrollIntoViewIfNeeded();
                    } else {
                        document.querySelector(".episode.active").scrollIntoView();
                    }
                }
            });
        },
        toggleSettingsMode(state) {
            state.settingsMode = true;
            state.statsMode = false;
            state.summaryMode = false;
            state.selectedEpisode = null;
        },
        toggleStatsMode(state) {
            state.settingsMode = false;
            state.statsMode = true;
            state.summaryMode = false;
            state.selectedEpisode = null;
        },
        toggleSummaryMode(state) {
            state.settingsMode = false;
            state.statsMode = false;
            state.summaryMode = true;
            state.selectedEpisode = null;
        },
        auth(state, payload) {
            state.avatar = payload.avatar;
            state.name = payload.name;
            state.screenName = payload.screen_name;
            state.bio = payload.bio;
            state.loggedIn = payload.loggedIn;
            state.isConfirmed = payload.isConfirmed;
            state.isContributor = payload.isContributor;
            state.isPrivileged = payload.isPrivileged;
            state.myId = payload.id;
            state.discordId = payload.discord_id;
            state.twitterId = payload.twitter_id;
            state.blueskyId = payload.bluesky_id;
            state.discordVisibility = payload.discord_visibility;
            state.twitterVisibility = payload.twitter_visibility;
            state.blueskyVisibility = payload.bluesky_visibility;
            state.userId = payload.userId;
            state.reports = payload.reports;
        },
        setIdBlacklist(state, payload) {
            state.idBlacklist = new Set(payload);
        },
        clearNotifications(state) {
            state.prompts = [];
            state.alerts = [];
            state.confirms = [];
            state.conflicts = [];
            state.dualLanguageInputs = [];
            state.potentialMistakes = [];
            state.potentialMistakeSkips = new Set();
        },
        clearPotentialMistakeSkips(state) {
            state.potentialMistakeSkips = new Set();
        },
        addPotentialMistakeSkip(state, payload) {
            state.potentialMistakeSkips.add(payload);
        },
        addPrompt(state, payload) {
            if (state.prompts.filter(prompt => prompt.id === payload.id && !prompt.hidden).length > 0) {
                return;
            }

            state.prompts.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addAlert(state, payload) {
            if (state.alerts.filter(alert => alert.id === payload.id && !alert.hidden).length > 0) {
                return;
            }

            state.alerts.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addConfirm(state, payload) {
            if (state.confirms.filter(confirm => confirm.id === payload.id && !confirm.hidden).length > 0) {
                return;
            }

            state.confirms.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addConflict(state, payload) {
            if (state.conflicts.filter(conflict => conflict.id === payload.id && !conflict.hidden).length > 0) {
                return;
            }

            state.conflicts.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addPotentialMistake(state, payload) {
            if (state.potentialMistakes.filter(mistake => mistake.id === payload.id && !mistake.hidden).length > 0) {
                return;
            }

            state.potentialMistakes.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addDualLanguageInput(state, payload) {
            if (state.dualLanguageInputs.filter(input => input.id === payload.id && !input.hidden).length > 0) {
                return;
            }

            state.dualLanguageInputs.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        addLinkToPageModal(state, payload) {
            if (state.linkToPageModals.filter(m => m.id === payload.id && !m.hidden).length > 0) {
                return;
            }

            state.linkToPageModals.push({
                ...payload,
                zIndex: ++state.notificationZIndexCounter
            });
        },
        setStaffList(state, payload) {
            state.staffList = { ...payload };
        },
        updateStaffListProperty(state, { key, value }) {
            if (value === undefined) {
                delete state.staffList[key];
            } else {
                state.staffList[key] = value;
            }
        },
        updateStaffListSettings(state, settings) {
            state.staffList.settings = { ...state.staffList.settings, ...settings };
        },
        setLastSavedStaffList(state, payload) {
            state.lastSavedStaffList = payload;
        },
        showPopup(state, { message, type = 'success', timeout = 3000 }) {
            state.popupShown = true;
            state.popupMessage = message;
            state.popupType = type;

            if (timeout === -1) return;

            // Auto-hide the popup after timeout
            setTimeout(() => {
                state.popupShown = false;
            }, timeout);
        },
        hidePopup(state) {
            state.popupShown = false;
        },
        // Set a cache entry for KeyFrame translation requests
        setKeyFrameTranslationCache(state, { key, value }) {
            state.keyFrameTranslationCache[key] = value;
        },
        // Set a cache entry for AniList translation requests
        // setAniListTranslationCache(state, { key, value }) {
        //     state.aniListTranslationCache[key] = value;
        // },
        changeFontSize(state, { which, size }) {
            const sideInRem = size / 16;

            if (which === 'native') {
                state.nativeSize = sideInRem;
                localStorage.setItem('nativeSize', sideInRem);
            } else if (which === 'english') {
                state.englishSize = sideInRem;
                localStorage.setItem('englishSize', sideInRem);
            }
        },
        changeFont(state, { which, font }) {
            if (which === 'native') {
                state.nativeFont = font;
                localStorage.setItem('nativeFont', font);
            } else if (which === 'english') {
                state.englishFont = font;
                localStorage.setItem('englishFont', font);
            }
        },
        changeSearchSystem(state, searchSystem) {
            state.searchSystem = searchSystem;
            localStorage.setItem('searchSystem', searchSystem);
        },
        changeStaffPageData(state, staffPageProvider) {
            state.staffPageProvider = staffPageProvider;
            localStorage.setItem('staffPageProvider', staffPageProvider);
        },
        changeDefaultEpisode(state, defaultEpisode) {
            state.defaultEpisode = defaultEpisode;
            localStorage.setItem('defaultEpisode', defaultEpisode);
        },
        changeAutoUnderline(state, autoUnderline) {
            state.autoUnderline = autoUnderline;
            localStorage.setItem('autoUnderline', autoUnderline);
        },
        changeShowTooltip(state, showTooltip) {
            state.showTooltip = showTooltip;
            localStorage.setItem('showTooltip', showTooltip);
        },
        changeShowTotalEpisodeCount(state, showTotalEpisodeCount) {
            state.showTotalEpisodeCount = showTotalEpisodeCount == '1';
            localStorage.setItem('showTotalEpisodeCount', showTotalEpisodeCount == '1' ? '1' : '0');
        },
        changeAiredDateFormat(state, airedDateFormat) {
            state.airedDateFormat = airedDateFormat;
            localStorage.setItem('airedDateFormat', airedDateFormat);
        },
        changeShowNativeTitle(state, showNativeTitle) {
            state.showNativeTitle = showNativeTitle == '1';
            console.log('Setting showNativeTitle to', showNativeTitle);
            localStorage.setItem('showNativeTitle', showNativeTitle == '1' ? '1' : '0');
        },
        initThemeMode(state) {
            const savedTheme = localStorage.getItem('themeMode');
            const validThemeMode = ['light', 'dark', 'system'].includes(savedTheme) ? savedTheme : 'system';

            state.themeMode = validThemeMode;
            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const shouldUseDark = validThemeMode === 'dark' || (validThemeMode === 'system' && prefersDark);

            state.resolvedTheme = shouldUseDark ? 'dark' : 'light';
            document.documentElement.classList.toggle('dark', shouldUseDark);
            document.documentElement.style.colorScheme = shouldUseDark ? 'dark' : 'light';
            localStorage.setItem('themeMode', validThemeMode);
        },
        setThemeMode(state, themeMode) {
            const validThemeMode = ['light', 'dark', 'system'].includes(themeMode) ? themeMode : 'system';
            state.themeMode = validThemeMode;
            localStorage.setItem('themeMode', validThemeMode);

            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const shouldUseDark = validThemeMode === 'dark' || (validThemeMode === 'system' && prefersDark);

            state.resolvedTheme = shouldUseDark ? 'dark' : 'light';
            document.documentElement.classList.toggle('dark', shouldUseDark);
            document.documentElement.style.colorScheme = shouldUseDark ? 'dark' : 'light';
        },
        cycleThemeMode(state) {
            const nextThemeMode = {
                light: 'dark',
                dark: 'system',
                system: 'light'
            }[state.themeMode] || 'system';

            state.themeMode = nextThemeMode;
            localStorage.setItem('themeMode', nextThemeMode);

            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const shouldUseDark = nextThemeMode === 'dark' || (nextThemeMode === 'system' && prefersDark);

            state.resolvedTheme = shouldUseDark ? 'dark' : 'light';
            document.documentElement.classList.toggle('dark', shouldUseDark);
            document.documentElement.style.colorScheme = shouldUseDark ? 'dark' : 'light';
        },
        syncSystemTheme(state) {
            if (state.themeMode !== 'system') {
                return;
            }

            const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const shouldUseDark = prefersDark;

            state.resolvedTheme = shouldUseDark ? 'dark' : 'light';
            document.documentElement.classList.toggle('dark', shouldUseDark);
            document.documentElement.style.colorScheme = shouldUseDark ? 'dark' : 'light';
        },
        resetFontSettings(state) {
            state.nativeSize = 14 / 16;
            state.nativeFont = 'inter';
            state.englishSize = 14 / 16;
            state.englishFont = 'inter';

            localStorage.removeItem('nativeSize');
            localStorage.removeItem('nativeFont');
            localStorage.removeItem('englishSize');
            localStorage.removeItem('englishFont');
        }
    }
});
